import React, { useRef, useState } from "react";
import styled from "styled-components/macro";

import Button from "ui/Button";
import UploadIcon from "ui/Icons/Upload";
import SunnySpinner from "ui/SunnySpinner";
import { theme } from "ui/theme";
import Typography from "ui/Typography";
import useToggle from "helpers/useToggle";
import ActionModal from "components/ActionModal";
import useImageUploader from "helpers/useImageUploader";
import { useUpdateProfileImageMutation } from "./UpdateProfileImage.generated";
import { SurfaceType, ActionType } from "utils/analytics/analyticsConstants";
import { ReactComponent as PencilIcon } from "images/pencil.svg";
import InitialProfileImageUploader, {
  $UploaderContainer,
} from "components/InitialProfileImageUploader";

import {
  AnalyticsProvider,
  useAnalytics,
} from "utils/analytics/AnalyticsContext";
import { hexColorWithAlpha } from "helpers/css";
import Avatar, { AvatarSize } from "ui/eds/Avatar";

type ProfileImageProps = {
  user: {
    id: string;
    name: string;
    profileImage?: {
      id: string;
      url: string;
    } | null;
  };
  analyticsSurface: SurfaceType;
  isInitialImageUploader?: boolean;
};

type CropImageArgs = {
  imgType: string;
  context: CanvasRenderingContext2D;
  imageObj: HTMLImageElement;
  sourceX: number;
  sourceY: number;
  sourceWidth: number;
  sourceHeight: number;
  destX: number;
  destY: number;
  destWidth: number;
  destHeight: number;
  canvas: HTMLCanvasElement;
};

const $AvatarContainer = styled.div`
  position: absolute;
  display: inline-block;
  margin-right: ${theme.SizeXl};
  clip-path: circle(3rem at center);
`;

const $ErrorMessage = styled(Typography).attrs({
  size: "bodySansSerif",
  textAlign: "center",
})`
  margin-top: 1rem;
`;

const $PreviewContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 1rem;
`;

const $CameraButtonContainer = styled.div`
  background: ${hexColorWithAlpha(theme.ColorSpace500, 0.3)};
  position: absolute;
  bottom: 0rem;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  max-height: 1.5rem;

  > *:first-child {
    top: -0.1rem;
  }
`;

const $UploadIcon = styled(UploadIcon)`
  margin-right: 0.25rem;
`;

export default function ProfileImage({
  user,
  analyticsSurface,
  isInitialImageUploader = false,
}: ProfileImageProps) {
  const [updateProfileImageMutation] = useUpdateProfileImageMutation();
  const [showUploadModal, toggleShowUploadModal] = useToggle(false);
  const [imageBlobURL, setImageBlobURL] = useState("");
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [previewError, setPreviewError] = useState(false);
  const [imageBlob, setImageBlob] = useState<Blob>();
  const { trackEvent } = useAnalytics("ProfileImage");
  // uses isInitialImageUploader to set initial state
  // isInitialImageUploaderVisible will determine whether we show the regular image uploader
  // or the InitialProfileImageUploader (image uploader with camera background)
  // InitialImageUploader will show in onboarding
  const [isInitialImageUploaderVisible, setIsInitialImageUploaderVisible] =
    useState(isInitialImageUploader);

  const inputRef = useRef<HTMLInputElement>(null);

  const { isImageUploading, uploadImage, imageUploadError, cancelImageUpload } =
    useImageUploader();

  const cropImage = ({
    imgType,
    context,
    imageObj,
    sourceX,
    sourceY,
    sourceWidth,
    sourceHeight,
    destX,
    destY,
    destWidth,
    destHeight,
    canvas,
  }: CropImageArgs) => {
    // returning promise here and resolving blob when the image finishes cropping so that
    // handleFileInputChange (where cropImage is called) will return blob when it resolves
    return new Promise<Blob>((resolve, reject) => {
      context.drawImage(
        imageObj,
        sourceX,
        sourceY,
        sourceWidth,
        sourceHeight,
        destX,
        destY,
        destWidth,
        destHeight,
      );
      canvas.toBlob((blob) => {
        if (!blob) {
          setPreviewError(true);
          return reject(new Error("Failed to crop image"));
        }

        setImageBlob(blob);
        const blobURL = URL.createObjectURL(blob);
        setImageBlobURL(blobURL);
        setLoadingPreview(false);

        resolve(blob);
      }, imgType);
    });
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Returning promise here so we can figure out when handleFileInputChange finishes.
    // For the InitialProfileImageUploader, we need to figure out when this resolves
    // before calling handleSubmitProfileImage which uploads the image
    return new Promise<Blob>((resolve, reject) => {
      const img = e.target.files?.[0];
      const canvas = document.createElement("canvas");
      if (!img || !canvas) {
        return;
      }
      const imgType = img.type;
      setLoadingPreview(true);
      const context = canvas.getContext("2d");
      const imageObj = new Image();
      imageObj.onload = () => {
        // determine shortest side of the image
        // set canvas height and width to be shortest side
        const shortestSide = Math.min(imageObj.width, imageObj.height);
        canvas.height = shortestSide;
        canvas.width = shortestSide;
        const isLandscape = imageObj.width > imageObj.height;
        // if picture is landscape, set the source x coordinate
        // to be the halfway mark of the canvas minus half of the source width (horizontally centers)
        // if picture is portrait, set the source x coordinate to be 0 (far left)
        // for a visual see: https://developer.mozilla.org/en-US/docs/Web/API/CanvasRenderingContext2D/drawImage
        const sourceX = isLandscape ? imageObj.width / 2 - shortestSide / 2 : 0;
        // set the source y coordinate to be at the top
        const sourceY = 0;
        // set the width and height of the sub-rectangle (this is used to crop)
        const sourceWidth = shortestSide;
        const sourceHeight = shortestSide;
        // determines placement of the cropped image on the destination canvas
        const destWidth = shortestSide;
        const destHeight = shortestSide;
        const destX = 0;
        const destY = 0;

        if (!context) {
          setPreviewError(true);
          return;
        }

        // resolving once the image is finish cropping on canvas
        cropImage({
          imgType,
          context,
          imageObj,
          sourceX,
          sourceY,
          sourceWidth,
          sourceHeight,
          destX,
          destY,
          destWidth,
          destHeight,
          canvas,
        })
          .then(resolve)
          .catch(reject);

        URL.revokeObjectURL(imageObjURL);
      };
      imageObj.onerror = () => {
        setPreviewError(true);
        URL.revokeObjectURL(imageObjURL);
        reject(new Error("Failed to load image"));
      };

      const imageObjURL = URL.createObjectURL(img);
      imageObj.src = imageObjURL;
    });
  };

  const handleCloseProfileImageModal = async () => {
    isImageUploading &&
      (await cancelImageUpload("user closed image upload modal"));
    URL.revokeObjectURL(imageBlobURL);
    setImageBlobURL("");
    setPreviewError(false);
    if (!isInitialImageUploaderVisible) {
      toggleShowUploadModal();
    }
  };

  const updateProfileImage = (imageId: string) => {
    updateProfileImageMutation({
      variables: { imageUploadId: imageId },
    })
      .then(() => {
        trackEvent({
          eventName: "upload_image",
          eventType: ActionType.CLICK_BUTTON,
          analyticsSurface: analyticsSurface,
        });
        handleCloseProfileImageModal();
      })
      .catch(() => {
        setPreviewError(true);
      });
  };

  const handleSubmitProfileImage = async (imageBlob?: Blob) => {
    if (imageBlob && !isImageUploading) {
      const imageId = await uploadImage(imageBlob);
      if (imageId) {
        updateProfileImage(imageId);
      } else {
        setPreviewError(true);
      }
    }
  };

  const showProfileImageModal = () => {
    setPreviewError(false);
    toggleShowUploadModal();
  };

  const isLoading = loadingPreview && !previewError;
  const isSubmitting = !imageBlobURL || loadingPreview || isImageUploading;
  const hasError = previewError || imageUploadError;

  async function onInitialProfileImageUploaderChange(
    event: React.ChangeEvent<HTMLInputElement>,
  ) {
    const imageBlob = await handleFileInputChange(event);
    await handleSubmitProfileImage(imageBlob);
    setIsInitialImageUploaderVisible(false);
  }

  function renderUploader() {
    if (isInitialImageUploaderVisible) {
      return (
        <InitialProfileImageUploader
          onFileInputChange={(event) =>
            onInitialProfileImageUploaderChange(event)
          }
          size={AvatarSize.xl}
        />
      );
    }

    if (imageBlobURL) {
      return (
        <$UploaderContainer $size={AvatarSize.xl}>
          <Avatar
            userId={user.id}
            size="xl"
            name={user.name}
            profileImage={imageBlobURL}
          />
        </$UploaderContainer>
      );
    }

    return (
      <$AvatarContainer>
        <Avatar
          userId={user.id}
          size="xl"
          name={user.name}
          profileImage={user.profileImage?.url}
        />
        <$CameraButtonContainer>
          <Button
            analyticsName="opened_image_upload_modal"
            onClick={showProfileImageModal}
            variant="link"
            requestInProgress={false}
            isOnDarkBackground
          >
            <PencilIcon role="img" aria-label="change profile picture" />
          </Button>
        </$CameraButtonContainer>
      </$AvatarContainer>
    );
  }

  return (
    <AnalyticsProvider surface={analyticsSurface}>
      {renderUploader()}
      {!isInitialImageUploaderVisible && (
        <ActionModal
          analyticsName="viewed_image_upload_modal"
          show={showUploadModal}
          close={{
            analyticsName: "closed_image_upload_modal",
            onClick: handleCloseProfileImageModal,
          }}
          title="Upload a profile picture"
        >
          <ActionModal.Body>
            {isLoading ? (
              <SunnySpinner />
            ) : (
              <$PreviewContainer>
                <Avatar
                  userId={user.id}
                  size="xl"
                  name={user.name}
                  profileImage={imageBlobURL || user.profileImage?.url}
                />
              </$PreviewContainer>
            )}
            <ActionModal.SecondaryButton
              analyticsName="clicked_upload_image"
              onClick={() => inputRef.current?.click()}
            >
              <$UploadIcon role="presentation" />
              Choose a file
            </ActionModal.SecondaryButton>
            <input
              type="file"
              onChange={handleFileInputChange}
              accept="image/png, image/jpeg, image/jpg"
              className="visually-hidden"
              tabIndex={-1}
              ref={inputRef}
            />
          </ActionModal.Body>
          <ActionModal.Footer>
            <ActionModal.PrimaryButton
              disabled={isSubmitting}
              onClick={() => handleSubmitProfileImage(imageBlob)}
              analyticsName="clicked_save_image"
              requestInProgress={isImageUploading}
            >
              Save image
            </ActionModal.PrimaryButton>
            {hasError && (
              <$ErrorMessage>
                Oops! We&rsquo;re having an issue. Please try again.
              </$ErrorMessage>
            )}
          </ActionModal.Footer>
        </ActionModal>
      )}
    </AnalyticsProvider>
  );
}
